import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from './lib/store.js';
import { Layout } from './components/Layout.js';
import { Loading } from './components/ui.js';
import { Logo } from './components/Logo.js';
import { useT } from './lib/i18n.js';

const AuthPage = lazy(() => import('./pages/Auth.js').then((m) => ({ default: m.AuthPage })));
const Dashboard = lazy(() => import('./pages/Dashboard.js').then((m) => ({ default: m.Dashboard })));
const Approvals = lazy(() => import('./pages/Approvals.js').then((m) => ({ default: m.Approvals })));
const Settings = lazy(() => import('./pages/Settings.js').then((m) => ({ default: m.Settings })));
const Billing = lazy(() => import('./pages/Billing.js').then((m) => ({ default: m.Billing })));
const ProjectResults = lazy(() => import('./pages/project/Results.js').then((m) => ({ default: m.ProjectResults })));
const ProjectOverview = lazy(() => import('./pages/project/Overview.js').then((m) => ({ default: m.ProjectOverview })));
const ProjectAgent = lazy(() => import('./pages/project/Agent.js').then((m) => ({ default: m.ProjectAgent })));
const ProjectAgentDetail = lazy(() => import('./pages/project/AgentDetail.js').then((m) => ({ default: m.ProjectAgentDetail })));
const ProjectAgentRun = lazy(() => import('./pages/project/AgentRun.js').then((m) => ({ default: m.ProjectAgentRun })));
const ProjectBuild = lazy(() => import('./pages/project/Build.js').then((m) => ({ default: m.ProjectBuild })));
const ProjectSettings = lazy(() => import('./pages/project/Settings.js').then((m) => ({ default: m.ProjectSettings })));
const ProjectQueue = lazy(() => import('./pages/project/Queue.js').then((m) => ({ default: m.ProjectQueue })));
const ProjectBuilder = lazy(() => import('./pages/project/Builder.js').then((m) => ({ default: m.ProjectBuilder })));
const ProjectAutopilot = lazy(() => import('./pages/project/Autopilot.js').then((m) => ({ default: m.ProjectAutopilot })));
const ProjectConnections = lazy(() => import('./pages/project/Connections.js').then((m) => ({ default: m.ProjectConnections })));
const MusicLibrary = lazy(() => import('./pages/Music.js').then((m) => ({ default: m.MusicLibrary })));
const ProjectSetup = lazy(() => import('./pages/project/Setup.js').then((m) => ({ default: m.ProjectSetup })));
const ProjectReview = lazy(() => import('./pages/project/Review.js').then((m) => ({ default: m.ProjectReview })));
const NewProjectPage = lazy(() => import('./pages/NewProject.js').then((m) => ({ default: m.NewProjectPage })));

/**
 * Alte Adressen führen auf die neuen (README §4.2). Lesezeichen dürfen nicht
 * ins Leere laufen -- der Rest der Adresse (etwa eine Lauf- oder Agenten-Id)
 * wird mitgenommen.
 */
function Moved({ to }: { to: (p: Record<string, string | undefined>) => string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={`${to(params)}${search}`} replace />;
}

export function App() {
  const { user, ready, bootstrap } = useAuth();
  const t = useT();

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  if (!ready) {
    return (
      <div
        style={{
          display: 'grid', placeItems: 'center', alignContent: 'center',
          gap: 18, minHeight: '100vh',
        }}
      >
        <Logo size={64} radius={20} />
        <Loading text={t('Sitzung wird geprüft…')} />
      </div>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={<Loading />}><Routes>
        <Route path="*" element={<AuthPage />} />
      </Routes></Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading />}><Routes>
      {/* Der Wizard läuft im Fokusmodus: ohne Seitenleiste, Topbar und Pipeline. */}
      <Route path="/new" element={<NewProjectPage />} />
      <Route path="/projects/:projectId/new" element={<Navigate to="/new" replace />} />

      <Route element={<Layout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/music" element={<MusicLibrary />} />

        <Route path="/projects/:projectId" element={<ProjectOverview />} />
        <Route path="/projects/:projectId/setup" element={<ProjectSetup />} />
        <Route path="/projects/:projectId/review" element={<ProjectReview />} />
        <Route path="/projects/:projectId/publish" element={<ProjectQueue />} />
        <Route path="/projects/:projectId/build" element={<ProjectBuild />} />
        <Route path="/projects/:projectId/run" element={<ProjectAgentRun />} />
        <Route path="/projects/:projectId/run/:runId" element={<ProjectAgentRun />} />
        <Route path="/projects/:projectId/results" element={<ProjectResults />} />
        <Route path="/projects/:projectId/automation" element={<ProjectAutopilot />} />
        <Route path="/projects/:projectId/recipes" element={<ProjectAgent />} />
        <Route path="/projects/:projectId/recipes/:agentId" element={<ProjectAgentDetail />} />
        <Route path="/projects/:projectId/channels" element={<ProjectConnections />} />
        <Route path="/projects/:projectId/builder" element={<ProjectBuilder />} />
        <Route path="/projects/:projectId/builder/:postId" element={<ProjectBuilder />} />

        {/* Alte Adressen */}
        <Route path="/projects/:projectId/agent" element={<Moved to={(p) => `/projects/${p.projectId}/recipes`} />} />
        <Route path="/projects/:projectId/agent/run" element={<Moved to={(p) => `/projects/${p.projectId}/run`} />} />
        <Route path="/projects/:projectId/agent/run/:runId" element={<Moved to={(p) => `/projects/${p.projectId}/run/${p.runId}`} />} />
        <Route path="/projects/:projectId/agents/:agentId" element={<Moved to={(p) => `/projects/${p.projectId}/recipes/${p.agentId}`} />} />
        <Route path="/projects/:projectId/settings" element={<Moved to={(p) => `/projects/${p.projectId}/setup`} />} />
        <Route path="/projects/:projectId/queue" element={<Moved to={(p) => `/projects/${p.projectId}/publish`} />} />
        <Route path="/projects/:projectId/autopilot" element={<Moved to={(p) => `/projects/${p.projectId}/automation`} />} />
        <Route path="/projects/:projectId/schedule" element={<Moved to={(p) => `/projects/${p.projectId}/automation`} />} />
        <Route path="/projects/:projectId/connections" element={<Moved to={(p) => `/projects/${p.projectId}/channels`} />} />
        <Route path="/projects/:projectId/approvals" element={<Moved to={(p) => `/projects/${p.projectId}/review`} />} />
        <Route path="/projects/:projectId/music" element={<Navigate to="/music" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes></Suspense>
  );
}
